import {
    ScreeningQuestion,
    ScreeningResult,
    ScreeningTool,
} from "../types/notes.types";

const FREQUENCY_OPTIONS = [
    "Not At All",
    "Several Days",
    "More Than Half the Days",
    "Nearly Every Day",
] as const;

const PHQ9_FREQUENCY_OPTIONS = [
    "Not at All",
    "Several Days",
    "More Than Half the Days",
    "Nearly Every Day",
] as const;

const DIFFICULTY_OPTIONS = [
    "Not Difficult at All",
    "Somewhat Difficult",
    "Very Difficult",
    "Extremely Difficult",
] as const;

const FUNCTIONAL_IMPACT_QUESTION =
    "If you checked off any problems, how difficult have these problems made it for you to do your work, take care of things at home, or get along with other people?";

const YES_NO_OPTIONS = ["Yes", "No"] as const;
const ASRS_OPTIONS = [
    "Never",
    "Rarely",
    "Sometimes",
    "Often",
    "Very Often",
] as const;

function questions(
    labels: string[],
    options: readonly string[]
): ScreeningQuestion[] {
    return labels.map((label, index) => ({
        id: `question_${index + 1}`,
        label,
        options,
    }));
}

export const SCREENING_TOOLS: ScreeningTool[] = [
    {
        id: "gad7",
        title: "GAD - 7 Anxiety",
        description:
            "Over the last 2 weeks, how often have you been bothered by the following problems?",
        sections: [
            {
                questions: [
                    ...questions(
                        [
                            "Feeling nervous, anxious, or on edge",
                            "Not being able to stop or control worrying",
                            "Worrying too much about different things",
                            "Trouble relaxing",
                            "Being so restless that it is hard to sit still",
                            "Becoming easily annoyed or irritable",
                            "Feeling afraid as if something awful might happen",
                        ],
                        FREQUENCY_OPTIONS
                    ),
                    {
                        id: "functional_impact",
                        label: FUNCTIONAL_IMPACT_QUESTION,
                        options: DIFFICULTY_OPTIONS,
                        required: false,
                    },
                ],
            },
        ],
    },
    {
        id: "phq9",
        title: "PHQ-9 Patient Health Questionnaire",
        description:
            "Over the last 2 weeks, how often have you been bothered by any of the following problems?",
        sections: [
            {
                questions: [
                    ...questions(
                        [
                            "Little interest or pleasure in doing things.",
                            "Feeling down, depressed, or hopeless.",
                            "Trouble falling or staying asleep, or sleeping too much.",
                            "Feeling tired or having little energy.",
                            "Poor appetite or overeating.",
                            "Feeling bad about yourself or that you are a failure or have let yourself or your family down.",
                            "Trouble concentrating on things, such as reading the newspaper or watching television.",
                            "Moving or speaking so slowly that other people could have noticed. Or the opposite, being so fidgety or restless that you have been moving around a lot more than usual.",
                            "Thoughts that you would be better off dead or of hurting yourself in some way.",
                        ],
                        PHQ9_FREQUENCY_OPTIONS
                    ),
                    {
                        id: "functional_impact",
                        label: FUNCTIONAL_IMPACT_QUESTION,
                        options: DIFFICULTY_OPTIONS,
                        required: false,
                    },
                ],
            },
        ],
    },
    {
        id: "asrs_v1_1",
        title: "ADULT ADHD SELF-REPORT SCALE (ASRS-V1.1) SYMPTOM CHECKLIST",
        description:
            "Please answer the questions below, rating yourself on each of the criteria shown using the scale Never - Very Often. Please indicate how you have felt and conducted yourself over the past six (6) months.",
        sections: [
            {
                title: "Part A",
                questions: questions(
                    [
                        "How often do you have trouble wrapping up the final details of a project, once the challenging parts have been done?",
                        "How often do you have difficulty getting things in order when you have to do a task that requires organization?",
                        "How often do you have problems remembering appointments or obligations?",
                        "When you have a task that requires a lot of thought, how often do you avoid or delay getting started?",
                        "How often do you fidget or squirm with your hands or feet when you have to be seated for a long time?",
                        "How often do you feel overly active and compelled to do things, as if you are driven by a motor?",
                    ],
                    ASRS_OPTIONS
                ),
            },
            {
                title: "Part B",
                questions: questions(
                    [
                        "How often do you make careless mistakes when you have to work on a boring or difficult project?",
                        "How often do you have difficulty keeping your attention when you are doing repetitive or boring work?",
                        "How often do you have difficulty concentrating on what people say to you, even when they are speaking to you directly?",
                        "How often do you misplace or have difficulty finding things at home or work?",
                        "How often are you distracted by activity or noise around you?",
                        "How often do you leave your seat in meetings or other situations in which you are expected to remain seated?",
                        "How often do you feel restless or fidgety?",
                        "How often do you have trouble unwinding and relaxing when you have time to yourself?",
                        "How often do you find yourself talking too much in a social situation?",
                        "When you're in a conversation, how often do you find yourself finishing the sentence of the person you are talking to, before they can finish it themselves?",
                        "How often do you have difficulty waiting your turn in situations when turn taking is required?",
                        "How often do you interrupt others when they are busy?",
                    ],
                    ASRS_OPTIONS
                ).map((question, index) => ({
                    ...question,
                    id: `question_${index + 7}`,
                    required: false,
                })),
            },
        ],
    },
    {
        id: "cage_aid",
        title: "CAGE - AID Questionnaire",
        description:
            "When thinking about drug use, include illegal drug use and the use of prescription drug use other than prescribed answer the following questions.",
        sections: [
            {
                questions: questions(
                    [
                        "Have you ever felt that you ought to cut down on your drinking or drug use?",
                        "Have people annoyed you by criticizing your drinking or drug use?",
                        "Have you ever felt bad or guilty about your drinking or drug use?",
                        "Have you ever had a drink or used drugs first thing in the morning to steady your nerves or to get rid of a hangover?",
                    ],
                    YES_NO_OPTIONS
                ),
            },
        ],
    },
    {
        id: "epds",
        title: "Edinburgh Postnatal Depression Scale (EPDS)",
        description: "In the past 7 days:",
        sections: [
            {
                questions: [
                    {
                        id: "question_1",
                        label: "I have been able to laugh and see the funny side of things",
                        options: [
                            "As Much as I Always Could",
                            "Not Quite so Much Now",
                            "Definitely Not so Much Now",
                            "Not at All",
                        ],
                    },
                    {
                        id: "question_2",
                        label: "I have looked forward with enjoyment to things",
                        options: [
                            "As Much as I ever Did",
                            "Rather Less than I Used to",
                            "Definitely Less than I Used to",
                            "Hardly at All",
                        ],
                    },
                    {
                        id: "question_3",
                        label: "I have blamed myself unnecessarily when things went wrong",
                        options: [
                            "Yes, Most of the Time",
                            "Yes, Some of the Time",
                            "Not Very Often",
                            "No, Never",
                        ],
                    },
                    {
                        id: "question_4",
                        label: "I have been anxious or worried for no good reason",
                        options: [
                            "No, Not at All",
                            "Hardly Ever",
                            "Yes, Sometimes",
                            "Yes, Very Often",
                        ],
                    },
                    {
                        id: "question_5",
                        label: "I have felt scared or panicky for no very good reason",
                        options: [
                            "Yes, Quite a Lot",
                            "Yes, Sometimes",
                            "No, Not Much",
                            "No, Not at All",
                        ],
                    },
                    {
                        id: "question_6",
                        label: "Things have been getting on top of me",
                        options: [
                            "Yes, most of the time I have not been able to cope at all",
                            "Yes, sometimes I have not been coping as well as usual",
                            "No, most of the time I have coped quite well",
                            "No, I have been coping as well as ever",
                        ],
                    },
                    {
                        id: "question_7",
                        label: "I have been so unhappy that I have had difficulty sleeping",
                        options: [
                            "Yes, Most of the Time",
                            "Yes, Sometimes",
                            "Not Very Often",
                            "No, Not at All",
                        ],
                    },
                    {
                        id: "question_8",
                        label: "I have felt sad or miserable",
                        options: [
                            "Yes, Most of the Time",
                            "Yes, Quite Often",
                            "Not Very Often",
                            "No, Not at All",
                        ],
                    },
                    {
                        id: "question_9",
                        label: "I have been so unhappy that I have been crying",
                        options: [
                            "Yes, Most of the Time",
                            "Yes, Quite Often",
                            "Only Occasionally",
                            "No, Never",
                        ],
                    },
                    {
                        id: "question_10",
                        label: "The thought of harming myself has occurred to me",
                        options: [
                            "Yes, Quite Often",
                            "Sometimes",
                            "Hardly Ever",
                            "Never",
                        ],
                    },
                ],
            },
        ],
    },
    {
        id: "mdq",
        title: "Mood Disorder Questionnaire",
        description:
            "Please answer the questions as best you can by putting a check in the appropriate box.",
        sections: [
            {
                title: "1. Has there ever been a period of time when you were not your usual self and ...",
                questions: questions(
                    [
                        "... you felt so good or so hyper that other people thought you were not your normal self or you were so hyper that you got into trouble?",
                        "... you were so irritable that you shouted at people or started fights or arguments?",
                        "... you felt much more self-confident than usual?",
                        "... you got much less sleep than usual and found that you didn't really miss it?",
                        "... you were more talkative or spoke much faster than usual?",
                        "... thoughts raced through your head or you couldn't slow your mind down?",
                        "... you were so easily distracted by things around you that you had trouble concentrating or staying on track?",
                        "... you had much more energy than usual?",
                        "... you were much more active or did many more things than usual?",
                        "... you were much more social or outgoing than usual; for example, you telephoned friends in the middle of the night?",
                        "... you were much more interested in sex than usual?",
                        "... you did things that were unusual for you or that other people might have thought were excessive, foolish, or risky?",
                        "... spending money got you or your family into trouble?",
                    ],
                    YES_NO_OPTIONS
                ).map((question) => ({ ...question, showNumber: false })),
            },
            {
                questions: [
                    {
                        id: "same_period",
                        label: "2. If you checked Yes to more than one of the above, have several of these ever happened during the same period of time?",
                        options: YES_NO_OPTIONS,
                    },
                    {
                        id: "problem_severity",
                        label: "3. How much of a problem did any of these cause you? (like being unable to work; having family, money, or legal troubles; and/or getting into arguments or fights)",
                        options: [
                            "No Problem",
                            "Minor Problem",
                            "Moderate Problem",
                            "Serious Problem",
                        ],
                    },
                ],
            },
        ],
    },
    {
        id: "ybocs",
        title: "Obsessive-Compulsive Test Yale Brown OCD Scale YBOCS",
        description:
            "Obsessions are frequent, unwelcome, and intrusive thoughts.",
        sections: [
            {
                questions: [
                    {
                        id: "question_1",
                        label: "How much time do you spend on obsessive thoughts?",
                        options: [
                            "(0) None",
                            "(1) 0-1 hrs/day",
                            "(2) 1-3 hrs/day",
                            "(3) 3-8 hrs/day",
                            "(4) More than 8 hrs/day",
                        ],
                    },
                    {
                        id: "question_2",
                        label: "How much do your obsessive thoughts interfere with your personal, social, or work life?",
                        options: [
                            "(0) None",
                            "(1) Mild",
                            "(2) Definite but manageable",
                            "(3) Substantial interference",
                            "(4) Severe",
                        ],
                    },
                    {
                        id: "question_3",
                        label: "How much do your obsessive thoughts distress you?",
                        options: [
                            "(0) None",
                            "(1) Little",
                            "(2) Moderate but manageable",
                            "(3) Severe",
                            "(4) Nearly constant, Disabling",
                        ],
                    },
                    {
                        id: "question_4",
                        label: "How hard do you try to resist your obsessions?",
                        options: [
                            "(0) Always try",
                            "(1) Try much of the time",
                            "(2) Try some of the time",
                            "(3) Rarely try. Often yield",
                            "(4) Never try.",
                        ],
                    },
                    {
                        id: "question_5",
                        label: "How much control do you have over your obsessive thoughts?",
                        options: [
                            "(0) Complete control",
                            "(1) Much control",
                            "(2) Some control",
                            "(3) Little control",
                            "(4) No control",
                        ],
                    },
                ],
            },
            {
                description:
                    "Compulsions are repetitive, behaviors or mental acts that you have a strong urge to repeat that are aimed at reducing your anxiety or preventing some dreaded event.",
                questions: [
                    {
                        id: "question_6",
                        label: "How much time do you spend performing compulsive behaviors?",
                        options: [
                            "(0) None",
                            "(1) 0-1 hrs/day",
                            "(2) 1-3 hrs/day",
                            "(3) 3-8 hrs/day",
                            "(4) More than 8 hrs/day",
                        ],
                    },
                    {
                        id: "question_7",
                        label: "How much do your compulsive behaviors interfere with your personal, social, or work life?",
                        options: [
                            "(0) None",
                            "(1) Mild",
                            "(2) Definite but manageable",
                            "(3) Substantial interference",
                            "(4) Severe",
                        ],
                    },
                    {
                        id: "question_8",
                        label: "How anxious would you feel if you were prevented from performing your compulsive behaviors?",
                        options: [
                            "(0) None",
                            "(1) Little",
                            "(2) Moderate but manageable",
                            "(3) Severe",
                            "(4) Nearly constant, Disabling",
                        ],
                    },
                    {
                        id: "question_9",
                        label: "How hard do you try to resist your compulsive behaviors?",
                        options: [
                            "(0) Always try",
                            "(1) Try much of the time",
                            "(2) Try some of the time",
                            "(3) Rarely try. Often yield",
                            "(4) Never try.",
                        ],
                    },
                    {
                        id: "question_10",
                        label: "How much control do you have over your compulsive behaviors?",
                        options: [
                            "(0) Complete control",
                            "(1) Much control",
                            "(2) Some control",
                            "(3) Little control",
                            "(4) No control",
                        ],
                    },
                ],
            },
        ],
    },
];

function totalFrequencyScore(
    responses: Record<string, string>,
    questionCount: number,
    options: readonly string[] = FREQUENCY_OPTIONS
) {
    return Array.from({ length: questionCount }, (_, index) => {
        const response = responses[`question_${index + 1}`];

        return options.indexOf(response);
    }).reduce<number>((total, score) => total + score, 0);
}

function getGad7Result(responses: Record<string, string>): ScreeningResult {
    const score = totalFrequencyScore(responses, 7);
    let interpretation = "Minimal";

    if (score >= 15) {
        interpretation = "Severe";
    } else if (score >= 10) {
        interpretation = "Moderate";
    } else if (score >= 5) {
        interpretation = "Mild";
    }

    return { score, maximumScore: 21, interpretation };
}

function getPhq9Result(responses: Record<string, string>): ScreeningResult {
    const score = totalFrequencyScore(responses, 9, PHQ9_FREQUENCY_OPTIONS);
    let interpretation = "None/minimal";

    if (score >= 20) {
        interpretation = "Severe";
    } else if (score >= 15) {
        interpretation = "Moderately severe";
    } else if (score >= 10) {
        interpretation = "Moderate";
    } else if (score >= 5) {
        interpretation = "Mild";
    }

    return { score, maximumScore: 27, interpretation };
}

function getAsrsResult(responses: Record<string, string>): ScreeningResult {
    const positiveCount = Array.from({ length: 6 }, (_, index) => {
        const response = responses[`question_${index + 1}`];
        const positiveOptions =
            index < 3
                ? ["Sometimes", "Often", "Very Often"]
                : ["Often", "Very Often"];

        return positiveOptions.includes(response) ? 1 : 0;
    }).reduce<number>((total, score) => total + score, 0);

    return {
        interpretation:
            positiveCount >= 4 ? "Positive screen" : "Negative screen",
        detail: `${positiveCount} of 6 Part A items positive`,
    };
}

function getCageAidResult(responses: Record<string, string>): ScreeningResult {
    const score = Array.from({ length: 4 }, (_, index) =>
        responses[`question_${index + 1}`] === "Yes" ? 1 : 0
    ).reduce<number>((total, answer) => total + answer, 0);

    return {
        score,
        maximumScore: 4,
        interpretation: score >= 1 ? "Positive screen" : "Negative screen",
    };
}

function getEpdsResult(responses: Record<string, string>): ScreeningResult {
    const tool = SCREENING_TOOLS.find(({ id }) => id === "epds");
    const forwardScoredQuestions = new Set([1, 2, 4]);
    const score =
        tool?.sections[0].questions.reduce((total, question, index) => {
            const optionIndex = question.options.indexOf(
                responses[question.id]
            );
            const questionNumber = index + 1;
            const questionScore = forwardScoredQuestions.has(questionNumber)
                ? optionIndex
                : 3 - optionIndex;

            return total + questionScore;
        }, 0) ?? 0;

    return {
        score,
        maximumScore: 30,
        interpretation:
            score >= 10 ? "Possible depression" : "Below screening cutoff",
    };
}

function getMdqResult(responses: Record<string, string>): ScreeningResult {
    const symptomCount = Array.from({ length: 13 }, (_, index) =>
        responses[`question_${index + 1}`] === "Yes" ? 1 : 0
    ).reduce<number>((total, answer) => total + answer, 0);
    const hasSymptomsDuringSamePeriod = responses.same_period === "Yes";
    const hasModerateOrSeriousProblem = [
        "Moderate Problem",
        "Serious Problem",
    ].includes(responses.problem_severity);
    const isPositive =
        symptomCount >= 7 &&
        hasSymptomsDuringSamePeriod &&
        hasModerateOrSeriousProblem;

    return {
        interpretation: isPositive ? "Positive screen" : "Negative screen",
        detail: `${symptomCount} of 13 symptom items endorsed`,
    };
}

function getYbocsResult(responses: Record<string, string>): ScreeningResult {
    const tool = SCREENING_TOOLS.find(({ id }) => id === "ybocs");
    const score =
        tool?.sections
            .flatMap(({ questions: sectionQuestions }) => sectionQuestions)
            .reduce(
                (total, question) =>
                    total + question.options.indexOf(responses[question.id]),
                0
            ) ?? 0;
    let interpretation = "Subclinical";

    if (score >= 32) {
        interpretation = "Extreme";
    } else if (score >= 24) {
        interpretation = "Severe";
    } else if (score >= 16) {
        interpretation = "Moderate";
    } else if (score >= 8) {
        interpretation = "Mild";
    }

    return { score, maximumScore: 40, interpretation };
}

export function calculateScreeningResult(
    tool: ScreeningTool,
    responses: Record<string, string>
): ScreeningResult {
    switch (tool.id) {
        case "gad7":
            return getGad7Result(responses);
        case "phq9":
            return getPhq9Result(responses);
        case "asrs_v1_1":
            return getAsrsResult(responses);
        case "cage_aid":
            return getCageAidResult(responses);
        case "epds":
            return getEpdsResult(responses);
        case "mdq":
            return getMdqResult(responses);
        case "ybocs":
            return getYbocsResult(responses);
        default:
            return { interpretation: "Not scored" };
    }
}

export function formatScreeningResult(result: ScreeningResult) {
    const score =
        result.score === undefined
            ? []
            : [
                  `Score: ${result.score}${
                      result.maximumScore === undefined
                          ? ""
                          : `/${result.maximumScore}`
                  }`,
              ];
    const detail = result.detail ? [result.detail] : [];

    return [...score, `Result: ${result.interpretation}`, ...detail];
}

export function formatScreeningResponses(
    tool: ScreeningTool,
    responses: Record<string, string>
) {
    const lines = tool.sections.flatMap((section) => {
        const heading = section.title ? [section.title] : [];
        const answers = section.questions
            .filter(({ id }) => responses[id])
            .map(({ id, label }) => `${label}: ${responses[id]}`);

        return [...heading, ...answers];
    });

    const result = calculateScreeningResult(tool, responses);

    return [tool.title, ...formatScreeningResult(result), ...lines].join("\n");
}
